import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listProtocolsFn,
  getProtocolFn,
  getStudyBuildFn,
  uploadProtocolFn,
  uploadProtocolAmendmentFn,
  updateBuildArtifactFn,
  approveStudyBuildFn,
  eSignStudyBuildFn,
  downloadProtocolFn,
  verifyProtocolChecksumFn,
  getApprovalMatrixFn,
  listBuildReviewsFn,
  listReviewCommentsFn,
  submitForReviewFn,
  reviewArtifactFn,
  addReviewCommentFn,
  resolveCommentFn,
  analyzeCrfEdgeCasesFn,
  listStudyBuildsFn,
  fileEtmfDocumentFn,
  listEtmfDocumentsFn,
  promoteToProdFn,
  cloneToTestFn,
  getPublishReadinessFn,
  getBuildHistoryFn,
} from "@/api/client";

export function useProtocols() {
  return useQuery({
    queryKey: ["protocols"],
    queryFn: () => listProtocolsFn({}),
    staleTime: 2 * 60 * 1000,
  });
}

export function useProtocol(protocolId: string | undefined) {
  return useQuery({
    queryKey: ["protocol", protocolId],
    queryFn: () => getProtocolFn({ protocolId: protocolId! }),
    enabled: !!protocolId,
    staleTime: 60 * 1000,
  });
}

export function useStudyBuild(buildId: string | undefined) {
  return useQuery({
    queryKey: ["studyBuild", buildId],
    queryFn: () => getStudyBuildFn({ buildId: buildId! }),
    enabled: !!buildId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUploadProtocol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadProtocolFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["protocols"] });
    },
  });
}

export function useUploadAmendment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadProtocolAmendmentFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["protocols"] });
      queryClient.invalidateQueries({ queryKey: ["protocol"] });
    },
  });
}

export function useUpdateBuildArtifact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateBuildArtifactFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studyBuild"] });
    },
  });
}

export function useApproveStudyBuild() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: approveStudyBuildFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studyBuild"] });
      queryClient.invalidateQueries({ queryKey: ["protocol"] });
    },
  });
}

export function useESignStudyBuild() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: eSignStudyBuildFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studyBuild"] });
    },
  });
}

export function useDownloadProtocol() {
  return useMutation({
    mutationFn: downloadProtocolFn,
  });
}

export function useVerifyChecksum() {
  return useMutation({
    mutationFn: verifyProtocolChecksumFn,
  });
}

// ── Approval Workflow ───────────────────────────────────

export function useApprovalMatrix(buildId: string | undefined) {
  return useQuery({
    queryKey: ["approvalMatrix", buildId],
    queryFn: () => getApprovalMatrixFn({ buildId: buildId! }),
    enabled: !!buildId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useBuildReviews(buildId: string | undefined) {
  return useQuery({
    queryKey: ["buildReviews", buildId],
    queryFn: () => listBuildReviewsFn({ buildId: buildId! }),
    enabled: !!buildId,
    staleTime: 30 * 1000,
  });
}

export function useReviewComments(
  buildId: string | undefined,
  artifactType: string,
  artifactId: string,
) {
  return useQuery({
    queryKey: ["reviewComments", buildId, artifactType, artifactId],
    queryFn: () =>
      listReviewCommentsFn({
        buildId: buildId!,
        artifactType,
        artifactId,
      }),
    enabled: !!buildId && !!artifactType && !!artifactId,
    staleTime: 15 * 1000,
  });
}

export function useSubmitForReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: submitForReviewFn,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["buildReviews", variables.buildId] });
      queryClient.invalidateQueries({ queryKey: ["studyBuild", variables.buildId] });
    },
  });
}

export function useReviewArtifact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reviewArtifactFn,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["buildReviews", variables.buildId] });
      queryClient.invalidateQueries({ queryKey: ["studyBuild", variables.buildId] });
    },
  });
}

export function useAddReviewComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addReviewCommentFn,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["reviewComments", variables.buildId, variables.artifactType, variables.artifactId],
      });
    },
  });
}

export function useResolveComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resolveCommentFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviewComments"] });
    },
  });
}

export function useAnalyzeCrfEdgeCases() {
  return useMutation({
    mutationFn: analyzeCrfEdgeCasesFn,
  });
}

export function useStudyBuilds() {
  return useQuery({
    queryKey: ["studyBuilds"],
    queryFn: () => listStudyBuildsFn({}),
    staleTime: 2 * 60 * 1000,
  });
}

// ── Environment operations ──────────────────────────────

export function usePromoteToProd() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: promoteToProdFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studyBuilds"] });
    },
  });
}

export function useCloneToTest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cloneToTestFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studyBuilds"] });
    },
  });
}

export function usePublishReadiness(buildId?: string) {
  return useQuery({
    queryKey: ["publishReadiness", buildId],
    queryFn: () => getPublishReadinessFn({ buildId: buildId! }),
    enabled: !!buildId,
    staleTime: 10 * 1000,
  });
}

export function useBuildHistory(buildId?: string) {
  return useQuery({
    queryKey: ["buildHistory", buildId],
    queryFn: () => getBuildHistoryFn({ buildId: buildId! }),
    enabled: !!buildId,
    staleTime: 30 * 1000,
  });
}

// ── eTMF Documents ───────────────────────────────────────

export function useEtmfDocuments(buildId?: string) {
  return useQuery({
    queryKey: ["etmfDocuments", buildId],
    queryFn: () => listEtmfDocumentsFn({ buildId: buildId! }),
    enabled: !!buildId,
    staleTime: 30 * 1000,
  });
}

export function useFileEtmfDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fileEtmfDocumentFn,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["etmfDocuments", variables.buildId] });
      queryClient.invalidateQueries({ queryKey: ["studyBuild", variables.buildId] });
    },
  });
}
